import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ideasAPI, statsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Lightbulb, Sparkles, ArrowRight, Target, FileText } from 'lucide-react';

export const CreateIdeaPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    target_market: ''
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await statsAPI.getCategories();
      setCategories(response.data.idea_categories);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e, generatePlan = false) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await ideasAPI.create(formData);
      const ideaId = response.data.id;

      if (generatePlan) {
        setGenerating(true);
        toast.info('Generating business plan with AI...', { duration: 5000 });
        
        try {
          await ideasAPI.generatePlan(ideaId);
          toast.success('Business plan generated successfully!');
        } catch (genError) {
          toast.error('Failed to generate plan. You can try again later.');
        }
        setGenerating(false);
      } else {
        toast.success('Idea created successfully!');
      }

      navigate(`/ideas/${ideaId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create idea');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.title && formData.description && formData.category;

  return (
    <div className="max-w-3xl mx-auto" data-testid="create-idea-page">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900">
          Create New Business Idea
        </h1>
        <p className="text-slate-600 mt-2">
          Share your business concept and let AI help you build a comprehensive plan.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-indigo-500" />
            Idea Details
          </CardTitle>
          <CardDescription>
            Provide as much detail as possible to get a better AI-generated business plan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-slate-700">
                Idea Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                placeholder="e.g., AI-Powered Personal Finance Assistant"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="h-12"
                data-testid="idea-title-input"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-slate-700">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => handleChange('category', value)}
              >
                <SelectTrigger className="h-12" data-testid="idea-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat} data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-slate-700">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                placeholder="Describe your business idea in detail. What problem does it solve? How does it work? What makes it unique?"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="min-h-[150px] resize-none"
                data-testid="idea-description-input"
              />
              <p className="text-xs text-slate-500">
                Tip: More detail helps the AI generate a better business plan.
              </p>
            </div>

            {/* Target Market */}
            <div className="space-y-2">
              <Label htmlFor="target_market" className="text-slate-700 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Target Market (Optional)
              </Label>
              <Input
                id="target_market"
                placeholder="e.g., Small business owners aged 25-45, millennials looking for budgeting tools"
                value={formData.target_market}
                onChange={(e) => handleChange('target_market', e.target.value)}
                className="h-12"
                data-testid="idea-target-market-input"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-12"
                onClick={(e) => handleSubmit(e, false)}
                disabled={loading || generating || !isFormValid}
                data-testid="save-draft-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                Save as Draft
              </Button>
              
              <Button
                type="button"
                className="flex-1 h-12 gradient-primary text-white btn-animate"
                onClick={(e) => handleSubmit(e, true)}
                disabled={loading || generating || !isFormValid}
                data-testid="generate-plan-btn"
              >
                {generating ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Generating Plan...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Generate AI Business Plan
                  </span>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="mt-6 bg-indigo-50/50 border-indigo-100">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="shrink-0">
              <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
            </div>
            <div>
              <h3 className="font-medium text-slate-900">AI-Powered Business Plans</h3>
              <p className="text-sm text-slate-600 mt-1">
                Our AI will analyze your idea and generate a comprehensive business plan including:
                market analysis, revenue models, marketing strategies, financial projections, and more.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateIdeaPage;
